import { EFileType } from 'constants/FileType';
import { EMIMETypes } from 'constants/MIMETypes';
import { RequestHeaderKeyTypes, RequestHeaderValueTypes } from 'constants/RequestHeaderTypes';
import { EFileTypes, queryString } from 'helpers/request';
import { isResponseError, TResponse } from 'models/apiClient';
import { ApiEndpoints } from 'models/apiEndpoints';
import {
  ILegalEntityWeaponLicence,
  ILegalEntityWeaponLicenceRenew,
  ILegalEntityWeaponLicenceVary,
  ILicenceActionRequest,
  ILicenceAttachmentsResponse,
  ILicenceBasicInfo,
  ILicenceData,
  ILicenceHistoryDetails,
  ILicenceHistoryResponse,
  ILicencePrintAvailability,
  ILicencePrintoutsHistoryResponse,
  ILicenceScheduledResponse,
  IRenewData,
  IRenewRequest,
  IVaryData,
  IVaryRequest,
} from 'models/licence';
import { apiClient } from 'utils/apiClient';
import { removeFileNameSpecialCharacters } from 'utils/files';

const getLicenceDetail = async () => {
  const { data } = await apiClient.get<TResponse<ILicenceData>>(
    ApiEndpoints.GET_LICENCE_DETAILS(),
  );
  if (isResponseError(data)) {
    return null;
  }
  return data;
};

const getLicencePrintoutsHistory = async (licenceId: string) => {
  const { data } = await apiClient.post<TResponse<ILicencePrintoutsHistoryResponse>>(
    ApiEndpoints.GET_LICENCE_PRINTOUTS_HISTORY(),
    { weaponLicenceUniqueId: licenceId },
  );
  if (!data || isResponseError(data)) {
    return null;
  }
  return data;
};

const getLicencePrintAvailability = async (licenceId: string) => {
  const { data } = await apiClient.get<TResponse<ILicencePrintAvailability>>(
    ApiEndpoints.GET_LICENCE_PRINT_AVAILABILITY(licenceId),
  );
  if (isResponseError(data)) {
    return null;
  }
  return data;
};

const cancelLicencePrintRequest = async (licencePrintUniqueId: string, version: number) => {
  const { data } = await apiClient.post<TResponse<null>>(
    `${ApiEndpoints.CANCEL_LICENCE_PRINT_REQUEST(licencePrintUniqueId)}?${queryString({
      version,
    })}`,
  );
  return !(data && isResponseError(data));
};

const enqueueLicencePrint = async (licenceId: string) => {
  const { data } = await apiClient.post<TResponse<null>>(
    ApiEndpoints.ENQUEUE_LICENCE_PRINT(licenceId),
  );
  return !isResponseError(data);
};

const redeemLicenceKey = async (key: any) => {
  const { data } = await apiClient.post(
    ApiEndpoints.REDEEM_LICENCE_KEY(),
    { key },
    {
      timeout: 30000,
    },
  );

  if (isResponseError(data)) {
    return null;
  }
  return data;
};

const getLicenceBasicInfo = async () => {
  try {
    const { data } = await apiClient.get<TResponse<ILicenceBasicInfo>>(
      ApiEndpoints.GET_LICENCE_BASIC_INFO(),
    );
    if (isResponseError(data)) {
      return null;
    }
    if (!data || !Object.prototype.hasOwnProperty.call(data, 'licenceType')) {
      return null;
    }
    return data;
  } catch (e: any) {
    return {
      licenceType: '',
      expiryDate: null,
      includeWeaponLicenceModule: false,
    };
  }
};

const getType = (fileType: string) => {
  switch (fileType) {
    case EMIMETypes.JPG:
      return EFileType.JPG;
    case EMIMETypes.JPEG:
      return EFileType.JPEG;
    case EMIMETypes.PNG:
      return EFileType.PNG;
    case EMIMETypes.PDF:
      return EFileType.PDF;
    case EMIMETypes.XLSX:
      return EFileType.XLSX;
    default:
      return '';
  }
};

const createWeaponLicence = async (request: ILegalEntityWeaponLicence, files: any) => {
  const config = {
    headers: {
      [RequestHeaderKeyTypes.CONTENT_TYPE]: RequestHeaderValueTypes.MULTIPART_FORM_DATA,
    },
    timeout: 0,
  };

  const preparedRequest = {
    ...request,
    // @ts-expect-error: required by update
    documents: request.documents?.map(({ file, ...rest }) => rest),
  };

  const formData = new FormData();
  formData.append(
    'parameters',
    new Blob([JSON.stringify(preparedRequest)], {
      type: EFileTypes.APPLICATION_JSON,
    }),
  );

  if (files.length) {
    for (let i = 0; i < files.length; i++) {
      const name = removeFileNameSpecialCharacters(files[i].name);
      formData.append('files', files[i], name);
    }
  }

  const { data } = await apiClient.post<TResponse<any>>(
    ApiEndpoints.CREATE_WEAPON_LICENCE(),
    formData,
    config,
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};
const varyWeaponLicence = async (
  request: ILegalEntityWeaponLicenceVary,
  files: any,
  id: string,
) => {
  const config = {
    headers: {
      [RequestHeaderKeyTypes.CONTENT_TYPE]: RequestHeaderValueTypes.MULTIPART_FORM_DATA,
    },
    timeout: 0,
  };

  const preparedRequest = {
    ...request,
    document: {
      ...request.document,
      // @ts-expect-error: required by update
      documents: request.document.documents?.map(({ file, ...rest }) => rest),
    },
  };

  const formData = new FormData();
  formData.append(
    'parameters',
    new Blob([JSON.stringify(preparedRequest)], {
      type: EFileTypes.APPLICATION_JSON,
    }),
  );

  if (files.length) {
    for (let i = 0; i < files.length; i++) {
      const name = removeFileNameSpecialCharacters(files[i].name);
      formData.append('files', files[i], name);
    }
  }

  const data = await apiClient.patch<TResponse<any>>(
    ApiEndpoints.VARY_WEAPON_LICENCE(id),
    formData,
    config,
  );

  return data;
};

const renewWeaponLicence = async (
  request: ILegalEntityWeaponLicenceRenew,
  files: any,
  id: string,
) => {
  const config = {
    headers: {
      [RequestHeaderKeyTypes.CONTENT_TYPE]: RequestHeaderValueTypes.MULTIPART_FORM_DATA,
    },
    timeout: 0,
  };

  const preparedRequest = {
    ...request,
    // @ts-expect-error: required by update
    documents: request.documents?.map(({ file, ...rest }) => rest),
  };

  const formData = new FormData();
  formData.append(
    'parameters',
    new Blob([JSON.stringify(preparedRequest)], {
      type: EFileTypes.APPLICATION_JSON,
    }),
  );

  if (files.length) {
    for (let i = 0; i < files.length; i++) {
      const name = removeFileNameSpecialCharacters(files[i].name);
      formData.append('files', files[i], name);
    }
  }

  const data = await apiClient.patch<TResponse<any>>(
    ApiEndpoints.RENEW_WEAPON_LICENCE(id),
    formData,
    config,
  );

  return data;
};

const getLicenceAttachments = async (
  weaponLicenceUniqueId: string,
): Promise<ILicenceAttachmentsResponse | null> => {
  const { data } = await apiClient.get<TResponse<ILicenceAttachmentsResponse>>(
    ApiEndpoints.GET_LICENCE_ATTACHMENTS(weaponLicenceUniqueId),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const getLicenceAttachmentFileData = async (
  weaponLicenceUniqueId: string,
  attachmentUniqueId: string,
  type: 'download' | 'view',
): Promise<any | null> => {
  const response =
    type === 'download'
      ? await apiClient.get(
          ApiEndpoints.GET_LICENCE_ATTACHMENT_FILE(weaponLicenceUniqueId, attachmentUniqueId),
        )
      : await apiClient.get(
          ApiEndpoints.GET_LICENCE_ATTACHMENT_FILE_VIEW(
            weaponLicenceUniqueId,
            attachmentUniqueId,
          ),
        );

  if (response.status !== 200) {
    return null;
  }

  return response.data.url;
};

const revokeWeaponLicence = async (
  uniqueId: string,
  body: ILicenceActionRequest,
): Promise<boolean> => {
  const { data } = await apiClient.patch(ApiEndpoints.REVOKE_WEAPON_LICENCE(uniqueId), body);

  return !isResponseError(data);
};

const cancelWeaponLicence = async (
  uniqueId: string,
  body: ILicenceActionRequest,
): Promise<boolean> => {
  const { data } = await apiClient.patch(ApiEndpoints.CANCEL_WEAPON_LICENCE(uniqueId), body);

  return !isResponseError(data);
};

const suspendWeaponLicence = async (
  uniqueId: string,
  body: ILicenceActionRequest,
): Promise<boolean> => {
  const { data } = await apiClient.patch(ApiEndpoints.SUSPEND_WEAPON_LICENCE(uniqueId), body);

  return !isResponseError(data);
};

const surrenderWeaponLicence = async (
  uniqueId: string,
  body: ILicenceActionRequest,
): Promise<boolean> => {
  const { data } = await apiClient.patch(
    ApiEndpoints.SURRENDER_WEAPON_LICENCE(uniqueId),
    body,
  );

  return !isResponseError(data);
};

const reactivateWeaponLicence = async (
  uniqueId: string,
  body: ILicenceActionRequest,
): Promise<boolean> => {
  const { data } = await apiClient.patch(
    ApiEndpoints.REACTIVATE_WEAPON_LICENCE(uniqueId),
    body,
  );

  return !isResponseError(data);
};

const getLicenceHistory = async (
  licenceUniqueId: string,
): Promise<ILicenceHistoryResponse | null> => {
  const { data } = await apiClient.get<TResponse<ILicenceHistoryResponse>>(
    ApiEndpoints.GET_LICENCE_HISTORY(licenceUniqueId),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const getLicenceHistoryDetails = async (
  historyUniqueId: string,
): Promise<ILicenceHistoryDetails | null> => {
  const { data } = await apiClient.get<TResponse<ILicenceHistoryDetails>>(
    ApiEndpoints.GET_LICENCE_HISTORY_DETAILS(historyUniqueId),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const weaponLicenceScheduledData = async (
  licenceNumber: string,
): Promise<ILicenceScheduledResponse | null> => {
  const { data } = await apiClient.post<TResponse<ILicenceScheduledResponse>>(
    ApiEndpoints.GET_WEAPON_LICENSE_SCHEDULED(),
    {
      licenceNumber,
    },
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const getCaseRenewData = async (id: string) => {
  const { data } = await apiClient.get<TResponse<IRenewData>>(ApiEndpoints.GET_CASE_RENEW(id));

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const getCaseVaryData = async (id: string) => {
  const { data } = await apiClient.get<TResponse<IVaryData>>(ApiEndpoints.GET_CASE_VARY(id));

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const renewLicenceCreateCase = async (body: IRenewRequest) => {
  const { applicationFiles, documents, request } = body;

  const config = {
    headers: {
      [RequestHeaderKeyTypes.CONTENT_TYPE]: RequestHeaderValueTypes.MULTIPART_FORM_DATA,
    },
    timeout: 0,
  };
  const formData = new FormData();

  formData.append(
    'request',
    new Blob([JSON.stringify(request)], {
      type: 'application/json',
    }),
  );

  if (applicationFiles) {
    for (let i = 0; i < applicationFiles.length; i++) {
      formData.append('application_files', applicationFiles[i], applicationFiles[i].name);
    }
  }
  if (documents) {
    for (let i = 0; i < documents.length; i++) {
      formData.append('documents', documents[i], documents[i].name);
    }
  }

  const result = await apiClient.post<TResponse<any>>(
    ApiEndpoints.CASE_RENEW(),
    formData,
    config,
  );

  return result;
};
const varyLicenceCreateCase = async (body: IVaryRequest) => {
  const { documents, request } = body;

  const config = {
    headers: {
      [RequestHeaderKeyTypes.CONTENT_TYPE]: RequestHeaderValueTypes.MULTIPART_FORM_DATA,
    },
    timeout: 0,
  };
  const formData = new FormData();

  formData.append(
    'request',
    new Blob([JSON.stringify(request)], {
      type: 'application/json',
    }),
  );
  if (documents) {
    for (let i = 0; i < documents.length; i++) {
      formData.append('documents', documents[i], documents[i].name);
    }
  }

  const result = await apiClient.post<TResponse<any>>(
    ApiEndpoints.CASE_VARY(),
    formData,
    config,
  );

  return result;
};

export {
  getLicenceDetail,
  getLicenceBasicInfo,
  redeemLicenceKey,
  createWeaponLicence,
  getLicenceAttachments,
  getLicenceAttachmentFileData,
  revokeWeaponLicence,
  cancelWeaponLicence,
  suspendWeaponLicence,
  surrenderWeaponLicence,
  reactivateWeaponLicence,
  varyWeaponLicence,
  renewWeaponLicence,
  getLicenceHistory,
  getLicenceHistoryDetails,
  weaponLicenceScheduledData,
  getCaseRenewData,
  getCaseVaryData,
  renewLicenceCreateCase,
  varyLicenceCreateCase,
  getLicencePrintAvailability,
  enqueueLicencePrint,
  getLicencePrintoutsHistory,
  cancelLicencePrintRequest,
};
